/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

import { knowledgeBaseActions } from './knowledgeBasesSlice';
import store from '../store';
import { apiSlice } from '../apiSlice';
import { ARTICLE_GROUP_IDS, KNOWLEDGE_BASE_SEARCH_BY_TYPES, KNOWLEDGE_BASE_URL_PARAMS } from '../../constants/knowledgeBases';
import { createSelector } from '@reduxjs/toolkit';

export const changePrimaryLanguage = (lang) => async (dispatch) => {
  return await dispatch(knowledgeBaseActions.knowledgeBasesPrimaryLanguage(lang));
};

export const changeSelectedLanguage = (lang) => async (dispatch) => {
  return await dispatch(knowledgeBaseActions.knowledgeBasesSelectedLanguage(lang));
};

export const clearSelectedLanguage = () => async (dispatch) => {
  return await dispatch(knowledgeBaseActions.clearKnowledgeBasesSelectedLanguage());
};

export const updateLocalKBArticlesCache = (articles) => (dispatch) => {
  const orgId = store.getState().auth.organizationId;
  const knowledgeBaseId = store.getState().knowledgeBases.knowledgeBaseId;

  const articlesCacheKey = {
    orgId,
    knowledgeBaseId,
    urlParams: KNOWLEDGE_BASE_URL_PARAMS.ARTICLES
  };

  store.dispatch(apiSlice.util.updateQueryData('fetchKnowledgeBaseArticles', articlesCacheKey, (draft) => {
    draft.items = [...articles.items];
  }));
};

export const updateSelectedArticle = (article) => (dispatch) => {
  return dispatch(knowledgeBaseActions.knowledgeBaseSelectedArticle(article));
};
export const addNewKnowledgeBaseGroupLocally = () => (dispatch) => {
  const orgId = store.getState().auth.organizationId;
  const knowledgeBaseId = store.getState().knowledgeBases.knowledgeBaseId;
  const primaryLanguage = store.getState().knowledgeBases.primaryLanguage;
  const groupCacheKey = {
    orgId,
    knowledgeBaseId,
    urlParams: KNOWLEDGE_BASE_URL_PARAMS.GROUPS
  };

  const newGroup = { id: ARTICLE_GROUP_IDS.NEW, name: { [primaryLanguage]: '' }, ordinal: 0 };

  store.dispatch(apiSlice.util.updateQueryData('fetchKnowledgeBaseGroups', groupCacheKey, (draft) => {
    draft.unshift(newGroup);
  }));
};

export const removeNewKnowledgeBaseGroupLocally = () => (dispatch) => {
  const orgId = store.getState().auth.organizationId;
  const knowledgeBaseId = store.getState().knowledgeBases.knowledgeBaseId;
  const groupCacheKey = {
    orgId,
    knowledgeBaseId,
    urlParams: KNOWLEDGE_BASE_URL_PARAMS.GROUPS
  };

  store.dispatch(apiSlice.util.updateQueryData('fetchKnowledgeBaseGroups', groupCacheKey, (draft) => {
    return draft.filter(group => group.id !== ARTICLE_GROUP_IDS.NEW);
  }));
};

export const updateKnowledgeBaseId = (value) => (dispatch) => {
  dispatch(knowledgeBaseActions.knowledgeBaseId(value));
};

export const updateKBFormDisabledStatus = (value) => (dispatch) => {
  dispatch(knowledgeBaseActions.knowledgeBaseFormDisabledStatus(value));
};

export const updateFormHasPendingChanges = (value) => (dispatch) => {
  dispatch(knowledgeBaseActions.knowledgeBaseFormHasPendingChanges(value));
};

export const updateIsPollingKBUploads = (value) => (dispatch) => {
  dispatch(knowledgeBaseActions.knowledgeBaseIsPollingKBUploads(value));
};

export const updateMissingTranslationInfo = (value) => (dispatch) => {
  dispatch(knowledgeBaseActions.knowledgeBaseMissingTranslationInfo(value));
};

export const selectFilteredArticlesData = createSelector(
  (result) => result.data,
  (_result, activeSearchFilters) => activeSearchFilters,
  (_result, _activeSearchFilters, selectedLanguage) => selectedLanguage,
  (_result, _activeSearchFilters, _sl, primaryLanguage) => primaryLanguage,
  (articlesData, searchFilters, selectedLanguage, primaryLanguage) => {
    if (!articlesData || !articlesData.items) {
      return { items: [], total: 0, ...articlesData };
    }

    const { searchTerm, searchByType } = searchFilters;

    if (!searchTerm || searchTerm.trim() === '' || searchByType === KNOWLEDGE_BASE_SEARCH_BY_TYPES.GROUP.key) {
      return {
        ...articlesData,
        items: sortArticles(articlesData?.items || [], selectedLanguage)
      };
    }

    let filteredItems = [...articlesData.items];

    const searchConfig = Object.values(KNOWLEDGE_BASE_SEARCH_BY_TYPES).find(
      type => type.key === searchByType
    );

    if (searchConfig && typeof searchConfig.filterFunction === 'function') {
      filteredItems = searchConfig.filterFunction(
        filteredItems,
        searchTerm,
        selectedLanguage,
        primaryLanguage
      );
    } else {
      console.warn(`Search configuration or filter function not found for type: ${searchByType}. Applying generic filter or no filter.`);
    }
    return { ...articlesData, items: sortArticles(filteredItems, selectedLanguage), total: filteredItems.length };
  }
);

const sortGroups = (groups) => {
  return [...groups].sort((a, b) => {
    const ordinalA = a.ordinal ?? 0;
    const ordinalB = b.ordinal ?? 0;
    return ordinalA - ordinalB;
  });
};

const sortArticles = (articles, language) => {
  return [...articles].sort((a, b) => {
    const contentA = a?.content?.find(c => c.languageCode === language);
    const contentB = b?.content?.find(c => c.languageCode === language);

    const titleA = contentA?.title || '';
    const titleB = contentB?.title || '';

    return titleA.localeCompare(titleB);
  });
};

export const selectProcessedGroupsData = createSelector(
  (result) => result.data,
  (_result, filteredArticleItems) => filteredArticleItems || [],
  (_result, _filteredArticleItems, activeSearchFilters) => activeSearchFilters,
  (_result, _filteredArticleItems, _activeSearchFilters, selectedLanguage) => selectedLanguage,
  (_result, _filteredArticleItems, _activeSearchFilters, _selectedLanguage, primaryLanguage) => primaryLanguage,
  (rawGroups, filteredArticleItems, searchFilters, selectedLanguage, primaryLanguage) => {
    const originalGroupsFromQuery = Array.isArray(rawGroups) ? rawGroups : [];

    const { searchTerm, searchByType } = searchFilters;

    if(!searchTerm) {
      return originalGroupsFromQuery;
    }

    if (searchTerm && filteredArticleItems.length === 0) {
      return [];
    }

    if(searchTerm && searchByType === KNOWLEDGE_BASE_SEARCH_BY_TYPES.GROUP.key) {
      let filteredGroups = [];
      const searchConfig = Object.values(KNOWLEDGE_BASE_SEARCH_BY_TYPES).find(
        type => type.key === searchByType
      );

      if (searchConfig && typeof searchConfig.filterFunction === 'function') {
        filteredGroups = searchConfig.filterFunction(
          [],
          searchTerm,
          selectedLanguage,
          primaryLanguage,
          originalGroupsFromQuery
        );
      }

      return filteredGroups;
    }

    const groupIdsInFilteredArticles = new Set(
      filteredArticleItems.map(article => article.groupId)
    );
    const resultingGroups = originalGroupsFromQuery.filter(group =>
      groupIdsInFilteredArticles.has(group.id)
    );

    return sortGroups(resultingGroups);
  }
);
